import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import UpdateIcon from '@mui/icons-material/Update';
import {
  Box,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect } from 'react';
import PageTransition from '../components/PageTransition';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const CGV: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', py: { xs: 6, md: 10 } }}>
          <Container maxWidth="md">
            <StyledPaper>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  mb: 4,
                  color: 'primary.main',
                  background: 'none',
                  WebkitBackgroundClip: 'unset',
                  WebkitTextFillColor: 'unset',
                }}
              >
                Conditions Générales de Vente
              </Typography>

              <Box sx={{ mt: 4 }}>
                <SectionTitle variant="h5">
                  <GavelIcon fontSize="large" />
                  1. Objet
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Les présentes conditions générales de vente régissent l'utilisation de la
                  plateforme Jukobox, un service de streaming musical en ligne permettant l'accès à
                  une bibliothèque de musique et de contenus audio.
                </Typography>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <CheckCircleOutlineIcon fontSize="large" />
                  2. Services proposés
                </SectionTitle>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Accès illimité à une bibliothèque musicale"
                      secondary="Des millions de titres disponibles en streaming"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Création de playlists personnalisées"
                      secondary="Organisez votre musique selon vos préférences"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Mode hors ligne"
                      secondary="Écoutez votre musique même sans connexion internet"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Recommandations personnalisées"
                      secondary="Découvrez de nouveaux artistes et titres"
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <PaymentIcon fontSize="large" />
                  3. Prix et paiement
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Jukobox propose plusieurs formules d'abonnement :
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Abonnement Premium"
                      secondary="9,99€/mois - Accès à toutes les fonctionnalités"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Abonnement Famille"
                      secondary="14,99€/mois - Jusqu'à 6 comptes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleOutlineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Abonnement Étudiant"
                      secondary="4,99€/mois - Tarif préférentiel pour les étudiants"
                    />
                  </ListItem>
                </List>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary', mt: 2 }}>
                  Le paiement s'effectue de manière sécurisée via notre plateforme de paiement. Les
                  prix sont indiqués en euros TTC.
                </Typography>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <SupportAgentIcon fontSize="large" />
                  4. Engagement de service
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Nous nous engageons à fournir un service de qualité et à maintenir la
                  disponibilité de la plateforme. En cas de maintenance, les utilisateurs seront
                  prévenus à l'avance par email et notification dans l'application.
                </Typography>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <SecurityIcon fontSize="large" />
                  5. Protection des données
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Conformément au RGPD, nous nous engageons à protéger les données personnelles des
                  utilisateurs. Pour plus d'informations, consultez notre politique de
                  confidentialité.
                </Typography>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <ErrorOutlineIcon fontSize="large" />
                  6. Responsabilité
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Jukobox ne peut être tenu responsable des interruptions de service dues à des
                  causes indépendantes de sa volonté. Nous nous engageons cependant à corriger toute
                  erreur de notre fait dans les plus brefs délais.
                </Typography>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                <SectionTitle variant="h5">
                  <UpdateIcon fontSize="large" />
                  7. Modification des CGV
                </SectionTitle>
                <Typography paragraph sx={{ pl: 2, color: 'text.secondary' }}>
                  Nous nous réservons le droit de modifier ces CGV à tout moment. Les utilisateurs
                  seront informés des modifications substantielles par email au moins 30 jours avant
                  leur entrée en vigueur.
                </Typography>
              </Box>
            </StyledPaper>
          </Container>
        </Box>
      </Box>
    </PageTransition>
  );
};

export default CGV;
